import { useState } from "react";
import SEOHead from "@/components/SEOHead";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

const contactSchema = z.object({
  first_name: z.string().trim().min(1, "First name is required").max(100),
  last_name: z.string().trim().min(1, "Last name is required").max(100),
  email: z.string().trim().email("Please enter a valid work address").max(255),
  company: z.string().trim().max(200).optional().or(z.literal("")),
  job_title: z.string().trim().max(200).optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message is required").max(5000),
  consent_data_processing: z.literal(true, {
    errorMap: () => ({ message: "You must agree to data processing" }),
  }),
  subscribe_newsletter: z.boolean(),
});

type ContactField =
  | "first_name"
  | "last_name"
  | "email"
  | "company"
  | "job_title"
  | "message"
  | "consent_data_processing"
  | "subscribe_newsletter";

const FIELD_IDS: Record<ContactField, string> = {
  first_name: "first-name",
  last_name: "last-name",
  email: "email",
  company: "company",
  job_title: "job-title",
  message: "message",
  consent_data_processing: "consent-data-processing",
  subscribe_newsletter: "subscribe-newsletter",
};

const Contact = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    job_title: "",
    message: "",
    consent_data_processing: false,
    subscribe_newsletter: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const updateField = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = contactSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0] as string;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setStatus("loading");
    const id = crypto.randomUUID();

    const { error } = await supabase.from("contact_submissions").insert({
      id,
      first_name: result.data.first_name.trim(),
      last_name: result.data.last_name.trim(),
      email: result.data.email.trim().toLowerCase(),
      company: result.data.company?.trim() || null,
      job_title: result.data.job_title?.trim() || null,
      message: result.data.message.trim(),
      consent_data_processing: true,
      subscribe_newsletter: result.data.subscribe_newsletter,
    });

    if (error) {
      setStatus("error");
      return;
    }

    // If opted into newsletter, also add to newsletter_subscribers
    if (result.data.subscribe_newsletter) {
      try {
        await supabase
          .from("newsletter_subscribers")
          .insert({ email: result.data.email.trim().toLowerCase() });
      } catch {
        // ignore duplicates
      }
    }

    // Send confirmation email (best-effort)
    const trimmedEmail = result.data.email.trim().toLowerCase();
    const trimmedName = result.data.first_name.trim();
    supabase.functions
      .invoke("send-transactional-email", {
        body: {
          templateName: "contact-confirmation",
          recipientEmail: trimmedEmail,
          idempotencyKey: `contact-confirm-${id}`,
          templateData: { name: trimmedName },
        },
      })
      .catch(() => {});
    // Notify team
    supabase.functions
      .invoke("send-transactional-email", {
        body: {
          templateName: "contact-notification",
          idempotencyKey: `contact-notify-${id}`,
          templateData: {
            name: `${trimmedName} ${result.data.last_name.trim()}`,
            email: trimmedEmail,
            company: result.data.company?.trim() || undefined,
            jobTitle: result.data.job_title?.trim() || undefined,
            message: result.data.message.trim(),
          },
        },
      })
      .catch(() => {});

    setStatus("success");
  };

  const inputClass =
    "w-full rounded-2xl border border-border bg-surface-panel px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors";
  const labelClass = "mb-1.5 block px-2 text-left text-xs font-medium text-foreground";
  const requiredMark = <span aria-hidden="true"> *</span>;
  const fieldA11y = (field: ContactField) => {
    const error = errors[field];

    return {
      id: FIELD_IDS[field],
      "aria-invalid": error ? true : undefined,
      "aria-describedby": error ? `${FIELD_IDS[field]}-error` : undefined,
    };
  };
  const renderError = (field: ContactField) =>
    errors[field] ? (
      <p id={`${FIELD_IDS[field]}-error`} role="alert" className="text-red-700 text-xs mt-1.5 px-2">
        {errors[field]}
      </p>
    ) : null;

  if (status === "success") {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <SEOHead title="Contact — Lumient" description="Get in touch with the Lumient team. Tell us about your use case and we'll get back to you within one business day." canonical="https://lumient.ai/contact" />
        <main className="flex-1 px-6 lg:px-16 pt-32 pb-24 max-w-3xl mx-auto w-full flex flex-col items-center justify-center text-center">
          <h1 className="text-3xl font-semibold tracking-tight mb-4 mt-6">Thank you for reaching out.</h1>
          <p className="text-muted-foreground text-base leading-relaxed max-w-md">
            We've received your message and will get back to you within one business day.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SEOHead title="Contact — Lumient" description="Get in touch with the Lumient team. Tell us about your use case and we'll get back to you within one business day." canonical="https://lumient.ai/contact" />
      <main className="flex-1 px-6 lg:px-16 pt-32 pb-24 max-w-2xl mx-auto w-full">
        <h1 className="text-4xl font-semibold tracking-tight mb-4">Get in touch</h1>
        <p className="text-muted-foreground text-base leading-relaxed mb-12 max-w-lg">
          We're a small team moving fast. Tell us about your use case and we'll get back to you
          within one business day.
        </p>

        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {/* Name row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor={FIELD_IDS.first_name} className={labelClass}>
                First name{requiredMark}
              </label>
              <input
                {...fieldA11y("first_name")}
                type="text"
                required
                placeholder="Ada"
                value={form.first_name}
                onChange={(e) => updateField("first_name", e.target.value)}
                className={inputClass}
              />
              {renderError("first_name")}
            </div>
            <div>
              <label htmlFor={FIELD_IDS.last_name} className={labelClass}>
                Last name{requiredMark}
              </label>
              <input
                {...fieldA11y("last_name")}
                type="text"
                required
                placeholder="Lovelace"
                value={form.last_name}
                onChange={(e) => updateField("last_name", e.target.value)}
                className={inputClass}
              />
              {renderError("last_name")}
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor={FIELD_IDS.email} className={labelClass}>
              Work address{requiredMark}
            </label>
            <input
              {...fieldA11y("email")}
              type="email"
              required
              placeholder="you@company.com"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              className={inputClass}
            />
            {renderError("email")}
          </div>

          {/* Company + Job Title row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor={FIELD_IDS.company} className={labelClass}>
                Company
              </label>
              <input
                {...fieldA11y("company")}
                type="text"
                placeholder="Lumient"
                value={form.company}
                onChange={(e) => updateField("company", e.target.value)}
                className={inputClass}
              />
              {renderError("company")}
            </div>
            <div>
              <label htmlFor={FIELD_IDS.job_title} className={labelClass}>
                Job title
              </label>
              <input
                {...fieldA11y("job_title")}
                type="text"
                placeholder="Operations lead"
                value={form.job_title}
                onChange={(e) => updateField("job_title", e.target.value)}
                className={inputClass}
              />
              {renderError("job_title")}
            </div>
          </div>

          {/* Message */}
          <div>
            <label htmlFor={FIELD_IDS.message} className={labelClass}>
              Your message{requiredMark}
            </label>
            <textarea
              {...fieldA11y("message")}
              required
              placeholder="Tell us what you want to keep current."
              rows={5}
              value={form.message}
              onChange={(e) => updateField("message", e.target.value)}
              className={`${inputClass} min-h-[120px] resize-none`}
            />
            {renderError("message")}
          </div>

          {/* Consent checkbox */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              {...fieldA11y("consent_data_processing")}
              type="checkbox"
              required
              checked={form.consent_data_processing}
              onChange={(e) => updateField("consent_data_processing", e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
            />
            <span className="text-sm text-muted-foreground leading-relaxed group-hover:text-foreground transition-colors">
              I agree to the storage and processing of my personal data to handle this enquiry.<span aria-hidden="true"> *</span>
            </span>
          </label>
          {renderError("consent_data_processing")}

          {/* Newsletter checkbox */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              {...fieldA11y("subscribe_newsletter")}
              type="checkbox"
              checked={form.subscribe_newsletter}
              onChange={(e) => updateField("subscribe_newsletter", e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
            />
            <span className="text-sm text-muted-foreground leading-relaxed group-hover:text-foreground transition-colors">
              Subscribe to our newsletter for product updates and early access opportunities.
            </span>
          </label>

          {status === "error" && (
            <p role="alert" className="text-red-700 text-sm">Something went wrong. Please try again.</p>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className="glow-button rounded-full bg-primary text-white px-8 py-3 text-sm font-medium tracking-wide transition-all duration-300 disabled:opacity-50"
          >
            {status === "loading" ? "Sending…" : "Send message"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default Contact;
